/**
 * AI Command — Responsible AI governance for the organization.
 *
 * The ecosystem in one workbench: every AI function built or bought is
 * REGISTERED, risk-tiered at intake by deterministic rules, ASSESSED with a
 * versioned impact assessment, DECIDED by the AI governance committee with
 * the decision recorded forever, watched by department CHAMPIONS, and every
 * ISSUE found in production documented against the system that produced it.
 *
 * Surfaces: compliance users get the full workbench; department users and
 * champions get propose-a-use-case, their own submissions, and issue logging.
 * Server enforces the same split; this shell only mirrors it.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { exportUseCaseRecord, exportProgramReport } from './exportAiGovernancePdf';
import { TIER_LABELS, useOrgUsers, OwnerPicker, ReadinessPanel, AssessmentView, SecurityReviewTab, TestingTab, ContingencyEditor, ProgramSettingsCard } from './governanceSections';
import { OwnerHome, MonitoringPill, MonitoringTab, MaterialChangeModal, RetirementCard } from './ownerSections';

// A link straight to one tool: /app?app=ai_command&tool=<id>, from the owner
// notices. Read once and removed from the address so Back never reopens it.
function takeToolParam() {
  try {
    const url = new URL(window.location.href);
    const id = url.searchParams.get('tool');
    if (!id) return null;
    url.searchParams.delete('tool');
    window.history.replaceState({}, '', url.toString());
    return /^[0-9a-f-]{36}$/i.test(id) ? id : null;
  } catch { return null; }
}
const REREVIEW_META = { label: 'Re-review required', cls: 'bg-amber-50 text-amber-700 ring-amber-200' };

const API = process.env.REACT_APP_API_URL || '';

// The three review paths, named as COM028 Section 2 names them.
const TIER_META = {
  full_review:   { label: TIER_LABELS.full_review,   cls: 'bg-red-50 text-red-700 ring-red-200' },
  fast_track:    { label: TIER_LABELS.fast_track,    cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
  register_only: { label: TIER_LABELS.register_only, cls: 'bg-slate-100 text-slate-600 ring-slate-200' },
};
const STATUS_META = {
  proposed:                    { label: 'Proposed',        cls: 'bg-sky-50 text-sky-700 ring-sky-200' },
  in_review:                   { label: 'In review',       cls: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  registered:                  { label: 'Registered',      cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  approved:                    { label: 'Approved',        cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  approved_with_contingencies: { label: 'Approved · contingencies', cls: 'bg-teal-50 text-teal-700 ring-teal-200' },
  declined:                    { label: 'Declined',        cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
  retired:                     { label: 'Retired',         cls: 'bg-slate-100 text-slate-500 ring-slate-200' },
};
const SEV_META = {
  low: 'bg-slate-100 text-slate-600', medium: 'bg-amber-50 text-amber-700',
  high: 'bg-orange-50 text-orange-700', critical: 'bg-red-50 text-red-700',
};
const KIND_LABELS = { internal_build: 'Internal build', vendor_tool: 'Vendor tool', embedded_feature: 'Embedded feature' };
const CHECK_LABELS = {
  model_card: 'Model card / HTI-1 attributes', attestation: 'Responsible AI attestation',
  local_validation: 'Local validation evidence', contract_clauses: 'Contract clauses',
  certifications: 'Certifications', incident_sla: 'Incident reporting obligations', exit_artifacts: 'Exit terms: data returned or destroyed',
  baa_addendum: 'Business Associate Agreement and AI Addendum',
};


const Pill = ({ meta, children }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ${meta?.cls || 'bg-slate-100 text-slate-600 ring-slate-200'}`}>
    {children || meta?.label}
  </span>
);
const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'; } catch { return '—'; } };

const SCREENS = [
  ['um_touching', 'Can it touch utilization management or coverage decisions?'],
  ['member_facing', 'Do members interact with it or receive its output?'],
  ['phi_access', 'Does it access PHI?'],
  ['uses_protected_inputs', 'Does it use protected characteristics (race, sex, age, disability) or proxies as inputs?'],
  ['autonomous_decisions', 'Does it act without a human approving each output?'],
  ['generative', 'Does it include a generative AI component?'],
];

// The six screens, the first thing on every record: each question in full
// with its answer, and compliance may correct an answer while the tool is
// still under review; the review path follows the facts (COM028 Sec 2).
function ScreensCard({ uc, compliance, ucId, onChanged }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const open = ['proposed', 'in_review', 'registered'].includes(uc.status);
  const flip = async (key) => {
    setBusy(key); setErr('');
    try {
      const r = await fetch(`${API}/api/ai-command/use-cases/${ucId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [key]: !uc[key] }) });
      const d = await r.json();
      if (!d.success) setErr(d.message || 'Could not save the answer.'); else onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  return (
    <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="text-xs font-bold tracking-wide text-orange-700 uppercase">The six screens that decide the review path</div>
        <div className="text-[11px] text-slate-500">{compliance && open ? 'Compliance may correct an answer; the path recomputes.' : compliance ? 'Answers are fixed once the tool is decided. Report a change to re screen it.' : 'Answered at intake; compliance corrects them.'}</div>
      </div>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1.5">
        {SCREENS.map(([key, q]) => (
          <div key={key} className="flex items-start justify-between gap-3 py-1 border-t border-orange-100/80">
            <span className={`text-sm ${uc[key] ? 'text-slate-900' : 'text-slate-600'}`}>{q}</span>
            {compliance && open ? (
              <button onClick={() => flip(key)} disabled={busy === key}
                className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-bold ring-1 transition-colors disabled:opacity-50 ${uc[key] ? 'bg-amber-100 text-amber-800 ring-amber-300' : 'bg-white text-slate-500 ring-slate-200 hover:text-slate-700'}`}>
                {uc[key] ? 'Yes' : 'No'}
              </button>
            ) : (
              <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-bold ring-1 ${uc[key] ? 'bg-amber-100 text-amber-800 ring-amber-300' : 'bg-white text-slate-500 ring-slate-200'}`}>{uc[key] ? 'Yes' : 'No'}</span>
            )}
          </div>
        ))}
      </div>
      {err && <p className="text-sm text-rose-600 mt-2">{err}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
export default function AICommandCenter({ user }) {
  const [initialTool] = useState(() => takeToolParam());
  const [view, setView] = useState(initialTool ? 'detail' : 'registry');          // registry | detail | settings
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState(initialTool);
  const [showIntake, setShowIntake] = useState(false);
  const [loadErr, setLoadErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/ai-command/dashboard`, { credentials: 'include' });
      const d = await r.json();
      if (d.success) { setData(d); setLoadErr(''); } else setLoadErr(d.message || 'Could not load AI Command.');
    } catch (e) { setLoadErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCase = (id) => { setSelectedId(id); setView('detail'); };

  if (loadErr) return <div className="p-10 text-sm text-rose-600">AI Command could not load: {loadErr}</div>;
  if (!data) return <div className="p-10 text-sm text-slate-400">Loading AI Command…</div>;

  // Department users and champions see exactly what they need: the door in,
  // where their proposals stand, nothing else. No registry, no committee,
  // no settings, no program mechanics.
  if (!data.compliance) {
    return (
      <div className="h-full overflow-auto bg-slate-50">
        <OwnerHome data={data} onOpen={openCase} onPropose={() => setShowIntake(true)}
          renderStatus={(uc) => <><Pill meta={STATUS_META[uc.status]} />{uc.material_change_at && uc.status === 'in_review' ? <Pill meta={REREVIEW_META} /> : null}</>} />
        {showIntake && (
          <IntakeModal onClose={() => setShowIntake(false)} onCreated={(id) => { setShowIntake(false); load(); openCase(id); }} />
        )}
        {view === 'detail' && selectedId && (
          <div className="fixed inset-0 z-40 bg-slate-50 overflow-auto">
            <div className="max-w-4xl mx-auto px-6 py-8">
              <UseCaseDetail id={selectedId} compliance={false}
                onBack={() => { setView('registry'); setSelectedId(null); load(); }} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] font-bold tracking-[0.14em] text-orange-600 uppercase mb-1">Precision Responsible AI</div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Responsible AI governance</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Every AI function this organization builds or buys is registered here, risk-tiered at intake,
              assessed, decided by the committee with the decision on the record, and watched after it ships.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {data.compliance && (
              <button onClick={async () => {
                const r = await fetch(`${API}/api/ai-command/program-report`, { credentials: 'include' });
                const rep = await r.json();
                if (rep.success) exportProgramReport({ report: rep });
              }}
                className="px-3.5 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-white transition-colors">
                Program report (PDF)
              </button>
            )}
            {data.compliance && (
              <button onClick={() => setView(view === 'settings' ? 'registry' : 'settings')}
                className={`px-3.5 py-2 rounded-xl border text-sm font-medium transition-colors ${view === 'settings' ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:bg-white'}`}>
                Committee &amp; champions
              </button>
            )}
            <button onClick={() => setShowIntake(true)}
              className="px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 transition-colors shadow-sm">
              Propose an AI use case
            </button>
          </div>
        </div>

        {view === 'detail' && selectedId ? (
          <UseCaseDetail id={selectedId} compliance={data.compliance}
            onBack={() => { setView('registry'); setSelectedId(null); load(); }} />
        ) : view === 'settings' && data.compliance ? (
          <SettingsPanel onChanged={load} />
        ) : (
          <Registry data={data} onOpen={openCase} />
        )}
      </div>

      {showIntake && (
        <IntakeModal onClose={() => setShowIntake(false)} onCreated={(id) => { setShowIntake(false); load(); openCase(id); }} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Registry({ data, onOpen }) {
  const s = data.stats;
  const [attention, setAttention] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  // Entities first, ideas beneath: the vendor that makes the tool, or the
  // organization for what it built in house. Largest entity on top.
  const groups = (() => {
    const by = new Map();
    for (const uc of data.use_cases) {
      const key = (uc.vendor_name || '').trim() || '__own';
      if (!by.has(key)) by.set(key, { key, label: key === '__own' ? 'Built in house' : key, items: [] });
      by.get(key).items.push(uc);
    }
    const out = [...by.values()].sort((a, b) => b.items.length - a.items.length || a.label.localeCompare(b.label));
    for (const g of out) {
      const n = (st) => g.items.filter((u) => u.status === st).length;
      const parts = [];
      const approved = n('approved') + n('approved_with_contingencies'); if (approved) parts.push(`${approved} approved`);
      const review = n('proposed') + n('in_review'); if (review) parts.push(`${review} in review`);
      if (n('registered')) parts.push(`${n('registered')} registered`);
      if (n('retired')) parts.push(`${n('retired')} retired`);
      if (n('declined')) parts.push(`${n('declined')} declined`);
      g.summary = parts.join(' · ');
    }
    return out;
  })();
  useEffect(() => {
    if (!data.compliance) return;
    let on = true;
    fetch(`${API}/api/ai-command/attention`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => { if (on && d.success) setAttention(d.items || []); })
      .catch(() => {});
    return () => { on = false; };
  }, [data.compliance]);
  const ATTN_LABELS = {
    rereview_overdue: 'Re-review overdue', rereview_soon: 'Re-review approaching',
    serious_issues: 'Serious issues open', open_contingencies: 'Contingencies open', stale_proposal: 'Proposal waiting',
    monitoring_owed: 'Monitoring owed', monitoring_review: 'Monitoring report to review',
    material_change: 'Material Change, re-review', retirement_requested: 'Retirement requested',
  };
  return (
    <>
      {attention && attention.length > 0 && (
        <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 mb-6">
          <div className="text-xs font-bold tracking-wide text-orange-700 uppercase mb-2">
            Needs attention · {attention.length}
          </div>
          <ul className="space-y-1.5">
            {attention.slice(0, 6).map((it, i) => (
              <li key={i}>
                <button onClick={() => onOpen(it.use_case_id)} className="text-left text-sm text-slate-800 hover:text-orange-800 transition-colors">
                  <span className="font-semibold">{it.name}</span>
                  <span className="text-orange-700"> · {ATTN_LABELS[it.kind] || it.kind}</span>
                  <span className="text-slate-500"> — {it.text}</span>
                </button>
              </li>
            ))}
          </ul>
          {attention.length > 6 && <div className="text-xs text-orange-700 mt-1.5">And {attention.length - 6} more below in the registry.</div>}
          <div className="text-[11px] text-orange-600/80 mt-2">These same items ride the daily attention brief email, one per day, only when the list is not empty.</div>
        </div>
      )}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          {[
            ['Registered systems', s.total, 'text-slate-900'],
            ['In review', s.in_review, 'text-indigo-600'],
            ['Approved', s.approved, 'text-emerald-600'],
            ['Open issues', s.open_issues, s.open_issues ? 'text-rose-600' : 'text-emerald-600'],
            ['Due for re-review', s.due_for_review, s.due_for_review ? 'text-amber-600' : 'text-slate-900'],
            ['Monitoring owed', s.monitoring_owed || 0, s.monitoring_owed ? 'text-amber-600' : 'text-slate-900'],
          ].map(([label, value, cls]) => (
            <div key={label} className="bg-white rounded-2xl border border-slate-200 px-4 py-3.5">
              <div className={`text-2xl font-bold tabular-nums ${cls}`}>{value}</div>
              <div className="text-[11px] font-semibold tracking-wide text-slate-400 uppercase mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-800">AI registry</h2>
          <span className="text-xs text-slate-400">{groups.length} entit{groups.length === 1 ? 'y' : 'ies'} · {data.use_cases.length} idea{data.use_cases.length === 1 ? '' : 's'}</span>
        </div>
        {data.use_cases.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Nothing registered yet. Every AI function starts here, before a line of code is written
              or a vendor contract is signed. Propose the first one.
            </p>
          </div>
        ) : groups.map((grp) => (
          <div key={grp.key} className="border-b border-slate-100 last:border-b-0">
            <button onClick={() => setCollapsed({ ...collapsed, [grp.key]: !collapsed[grp.key] })}
              className="w-full text-left px-5 py-3 bg-slate-50/80 hover:bg-slate-100 transition-colors flex items-center gap-3">
              <span className={`text-slate-400 transition-transform ${collapsed[grp.key] ? '' : 'rotate-90'}`}>›</span>
              <span className="text-sm font-bold text-slate-900">{grp.label}</span>
              <span className="text-xs text-slate-500">{grp.items.length} idea{grp.items.length === 1 ? '' : 's'}</span>
              <span className="text-[11px] text-slate-400 ml-auto">{grp.summary}</span>
            </button>
            {!collapsed[grp.key] && (
          <div className="divide-y divide-slate-100">
            {grp.items.map((uc) => (
              <button key={uc.id} onClick={() => onOpen(uc.id)}
                className="w-full text-left pl-10 pr-5 py-4 hover:bg-slate-50 transition-colors flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900 truncate">{uc.name}</span>
                    <Pill meta={STATUS_META[uc.status]} />
                    {uc.material_change_at && uc.status === 'in_review' && <Pill meta={REREVIEW_META} />}
                    <Pill meta={TIER_META[uc.risk_tier]} />
                    {uc.monitoring && ['due', 'overdue'].includes(uc.monitoring.state) && <MonitoringPill m={uc.monitoring} />}
                    {uc.open_serious_issues > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 ring-1 ring-red-200">
                        {uc.open_serious_issues} serious issue{uc.open_serious_issues === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 truncate">
                    {KIND_LABELS[uc.kind]}{uc.vendor_name ? ` · ${uc.vendor_name}` : ''}{uc.department ? ` · ${uc.department}` : ''}
                    {uc.next_review_date ? ` · re-review ${fmtDate(uc.next_review_date)}` : ''}
                  </div>
                </div>
                <div className="text-slate-300 shrink-0">›</div>
              </button>
            ))}
          </div>
            )}
          </div>
        ))}
      </div>

      {/* The ecosystem at a glance: who watches, who decides */}
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="text-xs font-bold tracking-wide text-slate-400 uppercase mb-3">Governance committee</h3>
          {data.committee?.length ? (
            <ul className="space-y-1.5">{data.committee.map((m, i) => (
              <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
                <span className="font-medium">{m.member_label}</span>
                <span className="text-xs text-slate-400 capitalize">{m.seat}</span>
              </li>
            ))}</ul>
          ) : <p className="text-sm text-slate-400">No committee seats named yet.</p>}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="text-xs font-bold tracking-wide text-slate-400 uppercase mb-3">Department champions</h3>
          {data.champions?.length ? (
            <ul className="space-y-1.5">{data.champions.map((c, i) => (
              <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                <span className="font-medium">{c.champion_label}</span>
                <span className="text-xs text-slate-400">{c.department}</span>
              </li>
            ))}</ul>
          ) : <p className="text-sm text-slate-400">No champions named yet. Champions are the eyes in each department: they bring AI ideas to intake before anything is built or bought.</p>}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
function IntakeModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: '', purpose: '', kind: 'internal_build', vendor_name: '', department: '', owner_label: '', owner_user_id: null, model_info: '', data_summary: '' });
  const users = useOrgUsers();
  const [flags, setFlags] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!f.name.trim()) { setErr('Name the use case.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/ai-command/use-cases`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...f, ...flags }),
      });
      const d = await r.json();
      if (d.success) onCreated(d.use_case.id);
      else setErr(d.message || 'Could not register the use case.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const input = 'w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300';
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center overflow-auto py-10 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Propose an AI use case</h2>
            <p className="text-xs text-slate-500 mt-0.5">Register it before you build it or sign with the vendor. Honest answers here decide the review path.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-500">Name *</label>
              <input className={input} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Prior authorization triage assistant" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-500">What does it do, and for whom?</label>
              <textarea className={input} rows={2} value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Kind</label>
              <select className={input} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                <option value="internal_build">Internal build</option>
                <option value="vendor_tool">Vendor tool</option>
                <option value="embedded_feature">Embedded feature in purchased software</option>
              </select>
            </div>
            {f.kind === 'vendor_tool' && (
              <div>
                <label className="text-xs font-semibold text-slate-500">Vendor</label>
                <input className={input} value={f.vendor_name} onChange={(e) => setF({ ...f, vendor_name: e.target.value })} />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-500">Department</label>
              <input className={input} value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} />
            </div>
            <OwnerPicker users={users} ownerUserId={f.owner_user_id} ownerLabel={f.owner_label} onChange={(o) => setF({ ...f, ...o })} />
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-500">Model / system notes</label>
              <input className={input} value={f.model_info} onChange={(e) => setF({ ...f, model_info: e.target.value })} placeholder="Provider, model, version, where it runs" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-500">Data in and out</label>
              <textarea className={input} rows={2} value={f.data_summary} onChange={(e) => setF({ ...f, data_summary: e.target.value })} />
            </div>
          </div>

          <div className="rounded-xl border border-orange-100 bg-orange-50/50 p-4">
            <div className="text-xs font-bold tracking-wide text-orange-700 uppercase mb-2">The screens that decide the review path</div>
            <div className="space-y-2">
              {SCREENS.map(([key, q]) => (
                <label key={key} className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 rounded border-slate-300 text-orange-600 focus:ring-orange-300"
                    checked={!!flags[key]} onChange={(e) => setFlags({ ...flags, [key]: e.target.checked })} />
                  <span>{q}</span>
                </label>
              ))}
            </div>
          </div>

          {err && <p className="text-sm text-rose-600">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={busy}
            className="px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-50">
            {busy ? 'Registering…' : 'Register for review'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function UseCaseDetail({ id, compliance, onBack }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState('overview');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [showDecision, setShowDecision] = useState(false);
  const [showIssue, setShowIssue] = useState(false);
  const [showChange, setShowChange] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/ai-command/use-cases/${id}`, { credentials: 'include' });
      const dd = await r.json();
      if (dd.success) setD(dd); else setErr(dd.message || 'Not found');
    } catch (e) { setErr(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="p-8 text-sm text-rose-600">{err}</div>;
  if (!d) return <div className="p-8 text-sm text-slate-400">Loading…</div>;
  const uc = d.use_case;

  const runAssessment = async () => {
    setBusy('assess'); setErr('');
    try {
      const r = await fetch(`${API}/api/ai-command/use-cases/${id}/assess`, { method: 'POST', credentials: 'include' });
      const dd = await r.json();
      if (!dd.success) setErr(dd.message || 'Assessment failed.');
      await load(); setTab('assessment');
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };

  // Champions see their case's status, its issues, and its documents.
  // The committee's working machinery is the committee's.
  const tier = d.readiness?.tier || uc.risk_tier;
  // The Business Owner works their tool: monitoring, issues, a change, retirement.
  const canWork = compliance || !!d.is_owner;
  const reportCount = (d.monitoring_reports || []).length;
  const monitoringTab = tier === 'register_only' ? [] : [['monitoring', `Monitoring (${reportCount})${d.monitoring && ['due', 'overdue'].includes(d.monitoring.state) ? ' !' : ''}`]];
  const changeable = ['approved', 'approved_with_contingencies', 'registered', 'in_review'].includes(uc.status);
  const tabs = compliance ? [
    ['overview', 'Overview'], ['assessment', `Assessment${d.assessments.length ? ` (v${d.assessments[0].version_number})` : ''}${tier !== 'register_only' && d.security_review?.status === 'complete' ? ' · security ✓' : ''}`],
    ...(tier === 'register_only' ? [] : [['testing', `Testing (${(d.test_records || []).length})`]]),
    ...monitoringTab,
    ['committee', `Committee (${d.decisions.length})`],
    ...(uc.kind === 'vendor_tool' ? [['vendor', `Vendor dossier (${d.vendor_checks.filter((c) => ['received', 'verified'].includes(c.status)).length}/${d.vendor_checks.length})`]] : []),
    ['issues', `Issues (${d.issues.filter((i) => i.status === 'open').length} open)`],
    ['documents', 'Documents'],
    ['framework', 'Framework alignment'],
    ['audit', 'Audit trail'],
  ] : [
    ['overview', 'Overview'],
    ...(d.is_owner ? monitoringTab : []),
    ['issues', `Issues (${d.issues.filter((i) => i.status === 'open').length} open)`],
    ['documents', 'Documents'],
  ];

  return (
    <div>
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700 mb-3">← Back to {compliance ? 'registry' : 'my AI tools'}</button>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-slate-900">{uc.name}</h2>
                <Pill meta={STATUS_META[uc.status]} />
                {uc.material_change_at && uc.status === 'in_review' && <Pill meta={REREVIEW_META} />}
                <Pill meta={TIER_META[uc.risk_tier]} />
                <MonitoringPill m={d.monitoring} />
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {KIND_LABELS[uc.kind]}{uc.vendor_name ? ` · ${uc.vendor_name}` : ''}{uc.department ? ` · ${uc.department}` : ''}
                {uc.owner_label ? ` · owner ${uc.owner_label}` : ''} · proposed {fmtDate(uc.created_at)}{uc.proposed_by_email ? ` by ${uc.proposed_by_email}` : ''}
              </div>
            </div>
            {canWork && (
              <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                {compliance && <button onClick={() => exportUseCaseRecord({ useCase: uc, goldenRules: d.golden_rules, assessments: d.assessments, decisions: d.decisions, vendorChecks: d.vendor_checks, issues: d.issues, nist: d.nist_alignment || [], securityReview: d.security_review, testRecords: d.test_records || [], readiness: d.readiness, monitoringReports: d.monitoring_reports || [], retirement: d.retirement })}
                  className="px-3.5 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50">
                  Export record (PDF)
                </button>}
                {changeable && <button onClick={() => setShowChange(true)}
                  className="px-3.5 py-2 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm font-semibold hover:bg-amber-100">
                  Report a change
                </button>}
                {compliance && <>
                <button onClick={runAssessment} disabled={busy === 'assess'}
                  className="px-3.5 py-2 rounded-xl border border-orange-200 bg-orange-50 text-orange-700 text-sm font-semibold hover:bg-orange-100 disabled:opacity-50">
                  {busy === 'assess' ? 'Drafting…' : d.assessments.length ? 'New assessment version' : 'Draft impact assessment'}
                </button>
                {tier !== 'register_only' && (
                  <button onClick={() => setShowDecision(true)}
                    className="px-3.5 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
                    Record committee decision
                  </button>
                )}
                </>}
              </div>
            )}
          </div>
          {err && <p className="text-sm text-rose-600 mt-2">{err}</p>}
          {uc.material_change_at && uc.status === 'in_review' && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900">
              <b>Material Change reported {fmtDate(uc.material_change_at)}.</b> {uc.material_change_note ? `${uc.material_change_note} ` : ''}
              {compliance ? 'Revise the assessment, repeat the testing, and return the tool to the committee before the change ships.' : 'Compliance revises the assessment and repeats the testing; the committee decides again before the change ships.'}
            </div>
          )}
        </div>

        <div className="px-6 border-b border-slate-100 flex gap-1 overflow-x-auto">
          {tabs.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === k ? 'border-orange-600 text-orange-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="px-6 py-5">
          {tab === 'overview' && (
            <div className="space-y-5">
              <ScreensCard uc={uc} compliance={compliance} ucId={id} onChanged={load} />
              {compliance && <ReadinessPanel d={d} ucId={id} compliance={compliance} onChanged={load} />}
              {!compliance && d.is_owner && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <div className="text-xs font-bold tracking-wide text-slate-400 uppercase mb-1">You are the Business Owner</div>
                  Monitoring reports, issues found in use, a change before it ships, and the notice when the tool is no longer needed are yours; each has its place on this page. Only compliance changes who owns a tool.
                </div>
              )}
              <RetirementCard d={d} ucId={id} compliance={compliance} canWork={canWork} onChanged={load} />
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Purpose" value={uc.purpose} />
                <Field label="Model / system" value={uc.model_info} />
                <Field label="Data in and out" value={uc.data_summary} />
                <Field label="Re-review" value={uc.next_review_date ? `${fmtDate(uc.next_review_date)} (every ${uc.review_cadence_months} months)` : 'Set on approval'} />
              </div>
              {d.golden_rules.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="text-xs font-bold tracking-wide text-amber-700 uppercase mb-2">Non-waivable rules this system engages</div>
                  <ul className="space-y-2">{d.golden_rules.map((r, i) => (
                    <li key={i} className="text-sm text-amber-900 flex gap-2"><span className="font-bold shrink-0">{i + 1}.</span><span>{r}</span></li>
                  ))}</ul>
                </div>
              )}
            </div>
          )}

          {tab === 'assessment' && <AssessmentView d={d} ucId={id} compliance={compliance} onChanged={load} />}
          {tab === 'assessment' && tier !== 'register_only' && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <h3 className="text-sm font-bold text-slate-800 mb-1">Security review (COM028 Section 5)</h3>
              <p className="text-xs text-slate-500 mb-4">Part of the assessment the committee reads: who may use the tool, where the data lives, retention, training on our data, logging, the vendor's security posture, and the conditions that become contingencies.</p>
              <SecurityReviewTab d={d} ucId={id} compliance={compliance} onChanged={load} />
            </div>
          )}
          {tab === 'testing' && <TestingTab d={d} ucId={id} compliance={compliance} onChanged={load} />}
          {tab === 'monitoring' && <MonitoringTab d={d} ucId={id} compliance={compliance} canWork={canWork} onChanged={load} />}

          {tab === 'committee' && (
            d.decisions.length ? (
              <ul className="space-y-3">{d.decisions.map((dec) => (
                <li key={dec.id} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Pill meta={STATUS_META[dec.decision] || { label: dec.decision.replace(/_/g, ' '), cls: 'bg-indigo-50 text-indigo-700 ring-indigo-200' }} />
                    <span className="text-xs text-slate-500">{fmtDate(dec.decided_at)} · {dec.decided_by_label || 'committee'}{dec.assessment_version ? ` · on assessment v${dec.assessment_version}` : ''}</span>
                    {dec.self_review && <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Sole-officer review, recorded</span>}
                  </div>
                  {dec.notes && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{dec.notes}</p>}
                  {Array.isArray(dec.contingencies) && dec.contingencies.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-bold tracking-wide text-slate-400 uppercase mb-1">
                        Contingencies · {dec.contingencies.filter((c) => c.done).length}/{dec.contingencies.length} complete
                      </div>
                      <ul className="space-y-1">{dec.contingencies.map((c, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                          {compliance ? (
                            <input type="checkbox" className="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-300 cursor-pointer"
                              checked={!!c.done}
                              onChange={async (e) => {
                                await fetch(`${API}/api/ai-command/use-cases/${id}/decisions/${dec.id}/contingencies/${i}`, {
                                  method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ done: e.target.checked }),
                                });
                                load();
                              }} />
                          ) : (
                            <span className={c.done ? 'text-emerald-500' : 'text-slate-300'}>{c.done ? '✓' : '○'}</span>
                          )}
                          <span className={c.done ? 'line-through text-slate-400' : ''}>{c.item}{c.owner ? ` — ${c.owner}` : ''}{c.due ? ` (due ${fmtDate(c.due)})` : ''}</span>
                        </li>
                      ))}</ul>
                    </div>
                  )}
                </li>
              ))}</ul>
            ) : <p className="text-sm text-slate-400">No committee decision yet. The decision, when made, is recorded here forever.</p>
          )}

          {tab === 'vendor' && (
            <ul className="space-y-2">{d.vendor_checks.map((c) => (
              c.item === 'attestation' && compliance
                ? <AttestationLinkRow key={c.id} check={c} ucId={id} onChanged={load} />
                : <VendorCheckRow key={c.id} check={c} ucId={id} compliance={compliance} onChanged={load} />
            ))}</ul>
          )}

          {tab === 'issues' && (
            <div>
              <div className="flex justify-end mb-3">
                <button onClick={() => setShowIssue(true)}
                  className="px-3.5 py-2 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold hover:bg-rose-100">
                  Document an issue
                </button>
              </div>
              {d.issues.length ? (
                <ul className="space-y-3">{d.issues.map((i) => (
                  <IssueRow key={i.id} issue={i} ucId={id} compliance={compliance} onChanged={load} />
                ))}</ul>
              ) : <p className="text-sm text-slate-400">No issues documented. When one is found in production, it belongs here — documented, owned, resolved.</p>}
            </div>
          )}

          {tab === 'documents' && (
            <DocumentsTab ucId={id} compliance={compliance} />
          )}

          {tab === 'framework' && (
            <div>
              <p className="text-sm text-slate-500 mb-4 max-w-2xl">
                Where this system stands against the NIST AI Risk Management Framework, computed from the governance
                record itself. A documented, framework-aligned record is the artifact examiners ask for, and in Texas
                it is a statutory safe harbor.
              </p>
              <div className="space-y-2">
                {(d.nist_alignment || []).map((n, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 p-3.5 flex items-start gap-3">
                    <span className={`shrink-0 mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${n.met ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      {n.met ? 'IN PLACE' : 'OWED'}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800">
                        <span className="text-orange-600 font-bold">{n.fn}</span> · {n.theme}
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5">{n.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'audit' && (
            <ul className="space-y-1.5">{d.audit.map((a, i) => (
              <li key={i} className="text-xs text-slate-500 flex gap-2">
                <span className="text-slate-400 tabular-nums shrink-0">{new Date(a.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                <span className="font-medium text-slate-700">{a.action.replace(/_/g, ' ')}</span>
              </li>
            ))}</ul>
          )}
        </div>
      </div>

      {showDecision && (
        <DecisionModal ucId={id} readiness={d.readiness} onClose={() => setShowDecision(false)} onDone={() => { setShowDecision(false); load(); setTab('committee'); }} />
      )}
      {showIssue && (
        <IssueModal ucId={id} onClose={() => setShowIssue(false)} onDone={() => { setShowIssue(false); load(); }} />
      )}
      {showChange && (
        <MaterialChangeModal d={d} ucId={id} onClose={() => setShowChange(false)} onDone={() => { setShowChange(false); load(); }} />
      )}
    </div>
  );
}

function DocumentsTab({ ucId, compliance }) {
  const [docs, setDocs] = useState(null);
  const [category, setCategory] = useState('other');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/ai-command/use-cases/${ucId}/documents`, { credentials: 'include' });
    const d = await r.json();
    if (d.success) setDocs(d.documents || []);
  }, [ucId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      const r = await fetch(`${API}/api/ai-command/use-cases/${ucId}/documents`, { method: 'POST', credentials: 'include', body: fd });
      const d = await r.json();
      if (!d.success) setErr(d.message || 'Upload failed.');
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const CAT_LABELS = { model_card: 'Model card', attestation: 'Attestation', validation: 'Validation', bias_testing: 'Bias testing', contract: 'Contract', other: 'Other' };
  const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4 max-w-2xl">
        The artifacts of governance: the vendor's model card, bias test results, validation reports, signed
        attestations, contracts. Stored durably with the record; every upload is audited.
      </p>
      {compliance && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <select className="px-3 py-2 rounded-xl border border-slate-200 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
            {Object.entries(CAT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label className="px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 cursor-pointer">
            {busy ? 'Uploading…' : 'Upload document'}
            <input type="file" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          {err && <span className="text-sm text-rose-600">{err}</span>}
        </div>
      )}
      {docs === null ? <p className="text-sm text-slate-400">Loading…</p>
        : docs.length === 0 ? <p className="text-sm text-slate-400">No documents yet.</p>
        : (
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
            {docs.map((doc) => (
              <li key={doc.id} className="px-4 py-3 flex items-center gap-3 bg-white">
                <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-50 text-orange-700 shrink-0">{CAT_LABELS[doc.category] || doc.category}</span>
                <a href={`${API}/api/ai-command/use-cases/${ucId}/documents/${doc.id}/download`} target="_blank" rel="noopener noreferrer"
                  className="text-sm font-semibold text-slate-800 hover:text-orange-700 truncate">
                  {doc.file_name}
                </a>
                <span className="text-xs text-slate-400 ml-auto shrink-0">{fmtSize(doc.file_size || 0)} · {fmtDate(doc.created_at)}{doc.uploaded_by_label ? ` · ${doc.uploaded_by_label}` : ''}</span>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs font-bold tracking-wide text-slate-400 uppercase mb-1">{label}</div>
    <p className="text-sm text-slate-800 whitespace-pre-wrap">{value || <span className="text-slate-300">Not stated</span>}</p>
  </div>
);

// The attestation dossier item is a LINK into FDR Oversight, where vendor
// attestations are collected (one front door for the vendor, one source of
// truth for the examiner). Linked, it reads the live attestation status.
function AttestationLinkRow({ check, ucId, onChanged }) {
  const [options, setOptions] = useState(null);
  const [picking, setPicking] = useState(false);
  const [systemId, setSystemId] = useState('');

  const loadOptions = async () => {
    const r = await fetch(`${API}/api/ai-command/fdr-link-options`, { credentials: 'include' });
    const d = await r.json();
    if (d.success) setOptions(d);
    setPicking(true);
  };
  const link = async () => {
    if (!systemId) return;
    const sys = options.ai_systems.find((s) => s.id === systemId);
    await fetch(`${API}/api/ai-command/use-cases/${ucId}/vendor-checks/${check.id}/link`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ai_system_id: systemId, fdr_entity_id: sys ? sys.entity_id : null }),
    });
    setPicking(false);
    onChanged();
  };

  const fl = check.fdr_link;
  const att = fl?.latest_attestation;
  return (
    <li className="rounded-xl border border-slate-200 p-3.5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800">Responsible AI attestation</div>
          <div className="text-xs text-slate-400 mt-0.5">Collected in FDR Oversight; this dossier reads the live status.</div>
        </div>
        {!check.linked_ai_system_id && !picking && (
          <button onClick={loadOptions} className="text-xs px-3 py-1.5 rounded-lg border border-orange-200 bg-orange-50 text-orange-700 font-semibold hover:bg-orange-100">
            Link to FDR Oversight
          </button>
        )}
      </div>

      {picking && options && (
        options.ai_systems.length ? (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <select className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs flex-1 min-w-[220px]" value={systemId} onChange={(e) => setSystemId(e.target.value)}>
              <option value="">Choose the vendor's AI system…</option>
              {options.ai_systems.map((s) => {
                const ent = options.entities.find((e2) => e2.id === s.entity_id);
                return <option key={s.id} value={s.id}>{ent ? `${ent.name} — ` : ''}{s.name} ({s.decision_function.replace(/_/g, ' ')}){s.latest_attestation_year ? ` · attested ${s.latest_attestation_year}` : ' · no attestation yet'}</option>;
              })}
            </select>
            <button onClick={link} disabled={!systemId} className="text-xs px-3 py-1.5 rounded-lg bg-slate-900 text-white font-semibold disabled:opacity-40">Link</button>
            <button onClick={() => setPicking(false)} className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500">Cancel</button>
          </div>
        ) : (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
            This vendor has no AI system registered under FDR Oversight yet. Add the vendor as an FDR, register the
            AI system on its AI tab, and collect the attestation there; then link it here.{' '}
            <a href="/app?app=fdr" className="font-semibold underline">Open FDR Oversight</a>
          </div>
        )
      )}

      {check.linked_ai_system_id && (
        <div className="mt-2.5 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
          <div className="text-xs text-slate-600">
            Linked to <span className="font-semibold text-slate-800">{fl?.fdr_name || 'FDR'}</span>
            {fl?.system_name ? <> · <span className="font-semibold text-slate-800">{fl.system_name}</span></> : null}
            {' '}· <a href="/app?app=fdr" className="text-orange-700 font-semibold hover:underline">Open in FDR Oversight</a>
          </div>
          {att ? (
            <div className="text-xs text-emerald-700 mt-1.5">
              Attested {att.attestation_year} by {att.attested_by_name}, {att.attested_by_title} ·
              {att.attests_human_in_loop ? ' Human in the loop ✓' : ' Human in the loop ✗'} ·
              {att.attests_individualized ? ' Individualized basis ✓' : ' Individualized basis ✗'} ·
              {att.attests_no_discrimination ? ' Non-discrimination ✓' : ' Non-discrimination ✗'}
            </div>
          ) : (
            <div className="text-xs text-amber-700 mt-1.5">No attestation on file yet for this system. Request it in FDR Oversight.</div>
          )}
        </div>
      )}
    </li>
  );
}

function VendorCheckRow({ check, ucId, compliance, onChanged }) {
  const [evidence, setEvidence] = useState(check.evidence || '');
  const setStatus = async (status) => {
    await fetch(`${API}/api/ai-command/use-cases/${ucId}/vendor-checks/${check.id}`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, evidence }),
    });
    onChanged();
  };
  const STATUS_CLS = { pending: 'text-slate-400', received: 'text-sky-600', verified: 'text-emerald-600', waived: 'text-slate-500', failed: 'text-rose-600' };
  return (
    <li className="rounded-xl border border-slate-200 p-3.5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-semibold text-slate-800">{CHECK_LABELS[check.item] || check.item}</div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold uppercase ${STATUS_CLS[check.status]}`}>{check.status}</span>
          {compliance && ['received', 'verified', 'waived', 'failed'].map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50 capitalize">{s}</button>
          ))}
        </div>
      </div>
      {compliance && (
        <input className="mt-2 w-full px-3 py-1.5 rounded-lg border border-slate-200 text-xs"
          placeholder="Evidence: what was received, where it lives, or why waived"
          value={evidence} onChange={(e) => setEvidence(e.target.value)} onBlur={() => evidence !== (check.evidence || '') && setStatus(check.status)} />
      )}
      {!compliance && check.evidence && <p className="text-xs text-slate-500 mt-1">{check.evidence}</p>}
    </li>
  );
}

function IssueRow({ issue, ucId, compliance, onChanged }) {
  const [resolution, setResolution] = useState(issue.resolution || '');
  const update = async (status) => {
    await fetch(`${API}/api/ai-command/use-cases/${ucId}/issues/${issue.id}`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, resolution }),
    });
    onChanged();
  };
  return (
    <li className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold uppercase ${SEV_META[issue.severity]}`}>{issue.severity}</span>
        <span className="text-sm font-semibold text-slate-900">{issue.title}</span>
        <span className={`text-xs font-semibold ${issue.status === 'open' ? 'text-rose-600' : 'text-emerald-600'}`}>{issue.status}</span>
        <span className="text-xs text-slate-400">found {fmtDate(issue.found_at)}{issue.found_by_label ? ` by ${issue.found_by_label}` : ''}</span>
      </div>
      {issue.severity === 'critical' && (
        <div className="text-xs text-red-700 mt-1">
          Critical Error: discovered {issue.discovered_at ? new Date(issue.discovered_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'not stated'}
          {issue.reported_at ? `, reported ${new Date(issue.reported_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
          {issue.compliance_notified_at ? ', compliance notified at once' : ''}
          {issue.resolved_at ? `, resolved ${fmtDate(issue.resolved_at)}` : ''}. The policy allows one business day from discovery to report.
        </div>
      )}
      {issue.description && <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap">{issue.description}</p>}
      {issue.resolution && <p className="text-sm text-emerald-800 mt-1.5"><b>Resolution:</b> {issue.resolution}</p>}
      {compliance && issue.status === 'open' && (
        <div className="mt-2 flex gap-2">
          <input className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs" placeholder="Resolution"
            value={resolution} onChange={(e) => setResolution(e.target.value)} />
          <button onClick={() => update('mitigated')} className="text-xs px-3 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 font-semibold">Mitigated</button>
          <button onClick={() => update('closed')} className="text-xs px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold">Closed</button>
        </div>
      )}
    </li>
  );
}

function DecisionModal({ ucId, readiness, onClose, onDone }) {
  const ready = !readiness || readiness.ready;
  const owed = (readiness?.items || []).filter((i) => !i.met);
  const [decision, setDecision] = useState(ready ? 'approved' : 're_review');
  const [notes, setNotes] = useState('');
  const [contingencies, setContingencies] = useState([{ item: '', owner: '', due: '' }]);
  const users = useOrgUsers();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/ai-command/use-cases/${ucId}/decision`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision, notes,
          contingencies: decision === 'approved_with_contingencies' ? contingencies.filter((c) => c.item.trim()) : [],
        }),
      });
      const d = await r.json();
      if (d.success) onDone(); else setErr(d.message || 'Could not record the decision.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center overflow-auto py-16 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">Record the committee decision</h2>
          <p className="text-xs text-slate-500 mt-0.5">Recorded forever against the assessment version of record.</p>
        </div>
        <div className="px-6 py-5 space-y-3">
          {!ready && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900">
              An approval may not be recorded yet. Still owed: {owed.map((i) => i.label).join('; ')}. The committee may return the tool for further review or decline it.
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {[['approved', 'Approve', !ready], ['approved_with_contingencies', 'Approve with contingencies', !ready], ['re_review', 'Return for further review', false], ['declined', 'Decline', false]].map(([v, label, off]) => (
              <button key={v} onClick={() => !off && setDecision(v)} disabled={off}
                className={`px-3 py-2.5 rounded-xl border text-sm font-medium text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${decision === v ? 'border-orange-500 bg-orange-50 text-orange-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {label}
              </button>
            ))}
          </div>
          {decision === 'approved_with_contingencies' && (
            <div>
              <label className="text-xs font-semibold text-slate-500">Contingencies: what, who, by when *</label>
              <ContingencyEditor rows={contingencies} onChange={setContingencies} users={users} />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-500">Committee notes</label>
            <textarea className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" rows={3}
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {err && <p className="text-sm text-rose-600">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
            {busy ? 'Recording…' : 'Record decision'}
          </button>
        </div>
      </div>
    </div>
  );
}

function IssueModal({ ucId, onClose, onDone }) {
  const [f, setF] = useState({ title: '', description: '', severity: 'medium', discovered_at: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!f.title.trim()) { setErr('Title the issue.'); return; }
    if (f.severity === 'critical' && !f.discovered_at) { setErr('Say when the Critical Error was discovered.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/ai-command/use-cases/${ucId}/issues`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
      });
      const d = await r.json();
      if (d.success) onDone(); else setErr(d.message || 'Could not document the issue.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center overflow-auto py-16 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">Document an issue</h2>
          <p className="text-xs text-slate-500 mt-0.5">Anyone can document an issue found in the wild. That is the point.</p>
        </div>
        <div className="px-6 py-5 space-y-3">
          <input className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" placeholder="What happened, in one line *"
            value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <textarea className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" rows={3} placeholder="Detail: what was observed, who was affected, when"
            value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <div className="flex gap-2">
            {['low', 'medium', 'high', 'critical'].map((s) => (
              <button key={s} onClick={() => setF({ ...f, severity: s })}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase transition-colors ${f.severity === s ? SEV_META[s] + ' ring-1 ring-current' : 'text-slate-400 hover:bg-slate-50'}`}>
                {s}
              </button>
            ))}
          </div>
          {f.severity === 'critical' && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 space-y-2">
              <div className="text-xs text-red-800">A Critical Error is an output that caused, or could reasonably have caused, harm to a member. It must reach compliance within one business day of discovery; the compliance team is told the moment you save this.</div>
              <div>
                <label className="text-xs font-semibold text-red-800">When was it discovered? *</label>
                <input type="datetime-local" className="w-full px-3 py-2 rounded-xl border border-red-200 text-sm bg-white" value={f.discovered_at} onChange={(e) => setF({ ...f, discovered_at: e.target.value })} />
              </div>
            </div>
          )}
          {err && <p className="text-sm text-rose-600">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50">
            {busy ? 'Saving…' : 'Document it'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function SettingsPanel({ onChanged }) {
  const [committee, setCommittee] = useState([]);
  const [champions, setChampions] = useState([]);
  const [newMember, setNewMember] = useState({ member_label: '', seat: 'member' });
  const [newChampion, setNewChampion] = useState({ department: '', champion_label: '' });

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/ai-command/dashboard`, { credentials: 'include' });
    const d = await r.json();
    if (d.success) { setCommittee(d.committee || []); setChampions(d.champions || []); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addMember = async () => {
    if (!newMember.member_label.trim()) return;
    await fetch(`${API}/api/ai-command/committee`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newMember) });
    setNewMember({ member_label: '', seat: 'member' }); load(); onChanged();
  };
  const addChampion = async () => {
    if (!newChampion.department.trim() || !newChampion.champion_label.trim()) return;
    await fetch(`${API}/api/ai-command/champions`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newChampion) });
    setNewChampion({ department: '', champion_label: '' }); load(); onChanged();
  };

  const input = 'px-3 py-2 rounded-xl border border-slate-200 text-sm';
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <ProgramSettingsCard />
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-slate-800 mb-1">Governance committee</h3>
        <p className="text-xs text-slate-500 mb-4">Cross-functional by design: compliance, privacy, security, clinical, legal, and the business. The chair runs the docket.</p>
        <div className="flex gap-2 mb-4">
          <input className={`${input} flex-1`} placeholder="Name or email" value={newMember.member_label}
            onChange={(e) => setNewMember({ ...newMember, member_label: e.target.value })} />
          <select className={input} value={newMember.seat} onChange={(e) => setNewMember({ ...newMember, seat: e.target.value })}>
            {['chair', 'privacy', 'security', 'clinical', 'legal', 'business', 'member'].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={addMember} className="px-3.5 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold">Add</button>
        </div>
        <ul className="space-y-1.5">{committee.map((m, i) => (
          <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
            <span className="font-medium">{m.member_label}</span>
            <span className="text-xs text-slate-400 capitalize">{m.seat}</span>
          </li>
        ))}</ul>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-slate-800 mb-1">Department champions</h3>
        <p className="text-xs text-slate-500 mb-4">The embedded first line. Champions spot AI being considered in their department and bring it to intake before it is built or bought.</p>
        <div className="flex gap-2 mb-4">
          <input className={`${input} flex-1`} placeholder="Department" value={newChampion.department}
            onChange={(e) => setNewChampion({ ...newChampion, department: e.target.value })} />
          <input className={`${input} flex-1`} placeholder="Champion name or email" value={newChampion.champion_label}
            onChange={(e) => setNewChampion({ ...newChampion, champion_label: e.target.value })} />
          <button onClick={addChampion} className="px-3.5 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold">Add</button>
        </div>
        <ul className="space-y-1.5">{champions.map((c, i) => (
          <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="font-medium">{c.champion_label}</span>
            <span className="text-xs text-slate-400">{c.department}</span>
          </li>
        ))}</ul>
      </div>
    </div>
  );
}
